import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { ConfirmAutoApproveOffDialog } from '@modules/users/components/AutoApproveUsersToggle/ConfirmAutoApproveOffDialog';
import { useToggleTenantAutoApproval } from '@modules/users/hooks';
import { useUserManagementStore } from '@modules/users/store/hooks';
import { UserManagementActionType } from '@modules/users/store/userManagementActionType';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import { CheckRounded } from '@mui/icons-material';
import {
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

enum AutoApproveSelectState {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export const AutoApproveUsersSelect = () => {
  const { openModal } = useMuiModalContext();
  const dispatch = useUserManagementStore(({ dispatch }) => dispatch);

  const { toggleAutoApproval } = useToggleTenantAutoApproval();

  const isAutoApprovalDisabled = useUserManagementStore(
    ({ isAutoApprovalDisabled }) => isAutoApprovalDisabled,
  );
  const isFetchStateRequestInProgress = useUserManagementStore(
    ({ visual }) => visual.isFetchingAutoApprovalState,
  );

  const selectedValue = isAutoApprovalDisabled
    ? AutoApproveSelectState.Disabled
    : AutoApproveSelectState.Enabled;

  const renderMenuItem = (value: AutoApproveSelectState) => {
    return (
      <MenuItem value={value} disabled={value === selectedValue}>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1">{value}</Typography>
          {value === selectedValue && <CheckRounded color="primary" />}
        </Stack>
      </MenuItem>
    );
  };

  const handleOptionChange = async (
    event: SelectChangeEvent<AutoApproveSelectState>,
  ) => {
    const selectedOption = event.target.value as AutoApproveSelectState;

    if (selectedOption === AutoApproveSelectState.Disabled) {
      openModal({
        contentComponent: () => <ConfirmAutoApproveOffDialog />,
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
        },
      });
      return;
    }

    await toggleAutoApproval(false);
    dispatch({
      type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
      payload: {
        isAutoApprovalDisabled: false,
      },
    });
    trackUserManagementEvents({
      eventName: 'User Management - Auto Approval Toggled On',
    });
  };

  return (
    <FormControlLabel
      control={
        <Select
          size="small"
          value={selectedValue}
          onChange={handleOptionChange}
          renderValue={(value) => value}
        >
          {renderMenuItem(AutoApproveSelectState.Enabled)}
          {renderMenuItem(AutoApproveSelectState.Disabled)}
        </Select>
      }
      disabled={isFetchStateRequestInProgress}
      label={
        <Stack>
          <Typography variant="body1">Auto-approve new users</Typography>
          <Typography variant="caption" color="text.secondary">
            When disabled, new users can only sign-up if invited to Enterpret
          </Typography>
        </Stack>
      }
      labelPlacement="start"
      sx={{
        direction: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 2,
      }}
    />
  );
};
